import { Moment } from 'moment';
import each from 'lodash/each';
import extend from 'lodash/extend';
import isFiniteNumber from 'lodash/isFinite';
import remove from 'lodash/remove';
import { Directive, EventEmitter, Input, NgZone, Output, OnInit, OnDestroy } from '@angular/core';
import { MapComponent, IKTMarker } from './map.component';
import { Session } from '../services/session';
import { gmapsLoadPromise } from '../lib/google-maps';
import {
  generateImage,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  IGeneratedImage,
  IGeneratedImageAnchor,
  IGeneratedImageShape,
} from './utils';
import { logd } from '../lib/utils';

type VehicleImageKeyType = 'marker-driving-eld-enabled-glow' | 'marker-driving-eld-enabled' | 'vehicle-stationary' | 'off-duty';
type DriverImageKeyType = 'driving-without-eld' | 'driver-on-duty' | 'off-duty';
type UpdateRecordType = 'current-location' | 'duty-status';

interface IRecordCurrentLocation {
  time: Moment;
  description: string;
  motionDescription: string;
  lat: number;
  lng: number;
  bearing?: number;
  moving?: boolean;
  isLive?: boolean;
}

interface IRecord {
  id: number;
  currentLocation: IRecordCurrentLocation;
  visible: boolean;
  dutyStatus?: string;
  showAlert?: boolean;
  recentDvir?: {
    id: number;
    defects: string[];
    hasValidStatus: boolean;
    hasSignature: boolean;
  };
  batteryCapacity?: number;
  isDvirStatusOk?: boolean;
  faultCodesCount?: number;
}

interface IIconConfiguration {
  url: string;
  anchor: google.maps.Point;
  scaledSize: google.maps.Size;
}

export interface IUpdatedRecord {
  id: number;
  currentLocation?: {
    time: Moment;
    description: string;
    lat: number;
    lng: number;
    motionDescription?: string;
    bearing?: number;
  };
  dutyStatus?: string;
}

interface ICachedMarkerRecord {
  record: IRecord;
  gmapsMarker: IKTMarker;
}

interface ICachedMarkerRecordHash {
  [key: number]: ICachedMarkerRecord;
}

interface IAnimationInfo {
  promise: Promise<void>;
  cancelAnimation: () => Promise<void>;
}

interface IDefaultUpdateRecordOption {
  noRepaint?: boolean;
  animationDuration?: number;
}

enum TAB_STATUS {
  ACTIVE = 'active',
  ACTIVE_AGAIN = 'active_again',
  ACTIVE_AGAIN_AFTER_ANIMATION_ENDS = 'active_again_after_animation_ends',
}

type AnimationPromiseType = void | string;

export const ANIMATE_ZOOM_LEVEL: number = 16;
const MAP_ZOOM: number = 16;
const unselectedMarkerZIndex: number = 1;
const selectedMarkerZIndex: number = 1000;
const ANIMATION_DURATION_THRESHOLD: number = 2 * 60 * 1000; // 2 mins

@Directive({
  selector: 'current-location-markers-list',
})
export class CurrentLocationMarkersListDirective implements OnInit, OnDestroy {
  @Input() public recordList: IRecord[] = [];
  @Input() public recordTitleKey: string;
  @Input() public type: 'vehicles' | 'assets' | 'drivers';
  @Input() public panWithAnimation: boolean = true;
  @Output() public onClick: EventEmitter<number> = new EventEmitter();
  private selectedId: number;
  private cachedMarkerRecordHash: ICachedMarkerRecordHash = {};
  private gmaps: any;

  constructor(private map: MapComponent, private ngZone: NgZone, private session: Session) {}

  public ngOnInit(): void {
    const promises = this.createMarkersPromise(this.recordList);
    this.map.markerPromiseList = promises;
    this.map.createMapPromise
      .then(() => {
        this.gmaps = this.map.gmaps;
        Promise.all(promises)
          .then(createdMarkersList => {
            this.map.addMarkersToCluster(createdMarkersList);
          })
        ;
      })
    ;
  }

  public ngOnDestroy(): void {
    this.map.markerPromiseList = [];
    const markersList: IKTMarker[] = [];
    each(this.cachedMarkerRecordHash, (markerRecord: ICachedMarkerRecord, id: string) => {
      const marker: IKTMarker = markerRecord.gmapsMarker;
      if (marker.cancelAnimation) marker.cancelAnimation();
      markersList.push(marker);
      this.gmaps.event.clearInstanceListeners(marker);
      this.map.deleteMarkerFromCache(id);
    });
    this.map.markerCluster.removeMarkers(markersList);
    if (this.selectedId) {
      const marker: IKTMarker = this.cachedMarkerRecordHash[this.selectedId].gmapsMarker;
      marker.setMap(null);
    }
  }

  public selectMarkerById(id: number = null): void {
    const deselectExistingSelectedMarker: (ktMarkerId: number, noRedraw: boolean) => Promise<void> =
      (ktMarkerId: number, noRedraw: boolean) => {
        return this.toggleSelectionOfMarker(ktMarkerId, false).then(() => {
          const selectedMarker: IKTMarker = this.cachedMarkerRecordHash[ktMarkerId].gmapsMarker;
          this.map.markerCluster.addMarker(selectedMarker, noRedraw);
        });
      }
    ;

    if (id == null || id === this.selectedId) {
      if (this.selectedId) {
        deselectExistingSelectedMarker(this.selectedId, true)
          .then(() => {
            const shouldSetZoom: boolean = this.shouldSetZoomForFitMapToBounds();
            this.map.fitMapToBounds(shouldSetZoom);
            this.selectedId = null;
            this.cancelAnimation();
          })
        ;
      }
    } else if (id !== this.selectedId) {
      if (this.selectedId) {
        deselectExistingSelectedMarker(this.selectedId, true);
      }

      const cachedMarkerRecord: ICachedMarkerRecord = this.cachedMarkerRecordHash[id];
      if (cachedMarkerRecord) {
        const { lat, lng } = cachedMarkerRecord.record.currentLocation;
        this.toggleSelectionOfMarker(id, true)
          .then(() => {
            this.map.markerCluster.removeMarker(cachedMarkerRecord.gmapsMarker);
            this.map.addExistingMarkerToMap(id);
            this.map.panIn({ lat, lng }, MAP_ZOOM, this.panWithAnimation);
          })
        ;
        this.selectedId = id;
      }
    }
  }

  public updateVisibilityOfMarkers(): void {
    let didLoopRun: boolean = false;
    each(this.cachedMarkerRecordHash, (cachedMarkerRecord: ICachedMarkerRecord, id: string) => {
      const record: IRecord = cachedMarkerRecord.record;
      const marker: IKTMarker = cachedMarkerRecord.gmapsMarker;
      marker.setVisible(record.visible);
      didLoopRun = true;
    });
    if (didLoopRun) this.map.markerCluster.resetViewport();
  }

  public updateRecords(
    updatedRecordsList: IUpdatedRecord[],
    updateType: UpdateRecordType,
    options: IDefaultUpdateRecordOption = {noRepaint: true, animationDuration: 0},
  ): Promise<string> {
    return new Promise(resolveMainPromise => {
      const promises: Array<Promise<any>> = [];
      each(updatedRecordsList, (updatedRecord: IUpdatedRecord) => {
        const cachedMarkerRecord: ICachedMarkerRecord = this.cachedMarkerRecordHash[updatedRecord.id];
        if (!cachedMarkerRecord) {
          logd('FV:', 'updateRecords | was not able to find cachedMarkerRecord');
          return;
        }

        const marker: IKTMarker = cachedMarkerRecord.gmapsMarker;
        const record: IRecord = cachedMarkerRecord.record;
        const { lat, lng, bearing, motionDescription }:
          { lat: number, lng: number, bearing?: number, motionDescription: string } = record.currentLocation;
        const isSelected: boolean = updatedRecord.id === this.selectedId;
        const dutyStatus: string = record.dutyStatus;

        if (updateType === 'current-location') {
          logd('FV:', 'updateRecords | updateType is current-location',
            'vehicle id: ', updatedRecord.id,
          );
          let imageLoadingPromise: Promise<void>;
          record.currentLocation.time = updatedRecord.currentLocation.time;
          record.currentLocation.description = updatedRecord.currentLocation.description;
          if (
            bearing !== updatedRecord.currentLocation.bearing ||
            motionDescription !== updatedRecord.currentLocation.motionDescription
          ) {
            logd('FV:', 'updateRecords | updating on the basis of motionDescription',
              'motionDescription', record.currentLocation.motionDescription,
              '; Updated motionDescription: ', updatedRecord.currentLocation.motionDescription,
              '; vehicle id: ', updatedRecord.id,
            );
            record.currentLocation.motionDescription = updatedRecord.currentLocation.motionDescription;
            if (this.type === 'vehicles' || this.type === 'assets') {
              logd('FV:', 'updateRecords | updating on the basis of bearing',
               'current location bearing: ', record.currentLocation.bearing,
               '; motion location bearing: ', updatedRecord.currentLocation.bearing,
               '; vehicle id: ', updatedRecord.id,
              );
              logd('FV:', 'updateRecords | calling generateAndSetImage',
                'vehicle id: ', updatedRecord.id,
              );
              record.currentLocation.bearing = updatedRecord.currentLocation.bearing;
              imageLoadingPromise = this.generateAndSetImage(record, marker, isSelected);
              promises.push(imageLoadingPromise);
            }
          }
          if (lat !== updatedRecord.currentLocation.lat || lng !== updatedRecord.currentLocation.lng) {
            logd('FV:', 'updateRecords | updating on the basis of lat and lng',
              'vehicle id: ', updatedRecord.id,
            );
            const updatedRecordPosition: google.maps.LatLng = this.getPositionForRecord(updatedRecord as IRecord);
            const isStationaryUpdate: boolean = this.isStationaryUpdate(updatedRecord as IRecord, isSelected);
            promises.push(
              this.updateMarkerByLocation(
                cachedMarkerRecord,
                updatedRecordPosition,
                isSelected,
                options.animationDuration,
                true,
                imageLoadingPromise,
                isStationaryUpdate,
              ),
            );
            record.currentLocation.lat = updatedRecord.currentLocation.lat;
            record.currentLocation.lng = updatedRecord.currentLocation.lng;
          }
        } else if (updateType === 'duty-status') {
          if (dutyStatus !== updatedRecord.dutyStatus) {
            record.dutyStatus = updatedRecord.dutyStatus;
            promises.push(this.generateAndSetImage(record, marker, isSelected));
          }
        }
      });
      Promise.all(promises).then((responseData: Array<string | undefined>) => {
        if (!options.noRepaint) this.map.repaintCluster();
        responseData = remove(responseData, undefined);
        const previousTabStatus: string = responseData.length != 0 ? responseData[0] : TAB_STATUS.ACTIVE;
        resolveMainPromise(previousTabStatus);
      });
    });
  }

  public cancelAnimation(): void {
    each(this.cachedMarkerRecordHash, (markerRecord: ICachedMarkerRecord, id: string) => {
      const marker: IKTMarker = markerRecord.gmapsMarker;
      if (marker.cancelAnimation) marker.cancelAnimation();
    });
  }

  public shouldSetZoomForFitMapToBounds(): boolean {
    const visiblePresentMarkersLength = Object.values(this.cachedMarkerRecordHash)
      .filter((markerRecord: ICachedMarkerRecord) => markerRecord.gmapsMarker.getVisible())
      .length
    ;
    return visiblePresentMarkersLength === 1;
  }

  private generateAndSetImage(record: IRecord, marker: IKTMarker, isSelected: boolean): Promise<void> {
    return this.generateImage(record, isSelected).then((iconDrawingInfo: IGeneratedImage) => {
      marker.setZIndex(this.getZIndex(isSelected));
      marker.setIcon(this.getIconConfiguration(iconDrawingInfo));
      marker.setShape(iconDrawingInfo.shape);
    });
  }

  private isStationaryUpdate(record: IRecord, isMarkerSelected: boolean): boolean {
    if (this.type === 'vehicles') {
      const imageKey: string = this.getVehicleImageKey(record, isMarkerSelected);
      if (imageKey === 'vehicle-stationary' || imageKey === 'off-duty') return true;
    }
    return false;
  }

  private updateMarkerByLocation(
    cachedMarkerObject: ICachedMarkerRecord,
    position: google.maps.LatLng,
    isSelected: boolean,
    animationDuration: number,
    noRedraw: boolean,
    imageLoadingPromise: Promise<void>,
    isStationaryUpdate: boolean,
  ): Promise<AnimationPromiseType> {
    const promise: Promise<AnimationPromiseType> = new Promise(resolve => {
      const marker: IKTMarker = cachedMarkerObject.gmapsMarker;
      const record: IRecord = cachedMarkerObject.record;
      const gmap: google.maps.Map = this.map.map;
      const currentZoomLevel: number = gmap.getZoom();
      let followMode: boolean = false;
      logd('FV:', 'inside updateMarkerByLocation for record with id of: ', record.id);
      if (currentZoomLevel >= ANIMATE_ZOOM_LEVEL && animationDuration != 0) {
        logd('FV:', 'updateMarkerByLocation | Animating Marker for vehicle with id of:', record.id);
        const mapViewPort: google.maps.LatLngBounds = gmap.getBounds();
        const currentPosition: google.maps.LatLng = this.getPositionForRecord(record);
        const updatedPosition: google.maps.LatLng = position;
        if (
          (mapViewPort.contains(currentPosition) || mapViewPort.contains(updatedPosition))
          && animationDuration < ANIMATION_DURATION_THRESHOLD
          && !isStationaryUpdate) {
          record.currentLocation.isLive = true;
          const onMarkerAnimationEnd = (value: AnimationPromiseType) => {
            record.currentLocation.isLive = false;
            resolve(value);
          };
          followMode = true;
          if (imageLoadingPromise) {
            imageLoadingPromise.then(() => {
              this.animateMarker(marker, position, animationDuration, followMode).then(onMarkerAnimationEnd);
            });
          } else {
            this.animateMarker(marker, position, animationDuration, followMode).then(onMarkerAnimationEnd);
          }
        } else {
          logd('FV:', 'updateMarkerByLocation | vehicle is not selected panning setting new position',
            '; vehicle id: ', record.id,
          );
          marker.setPosition(position);
          if (!noRedraw) {
            logd('FV:', 'updateMarkerByLocation | noRedraw is: ', !noRedraw,
              ' and vehicle id is: ', record.id,
            );
            this.map.markerCluster.repaint();
          }
          resolve();
        }
      } else {
        logd('FV:', 'updateMarkerByLocation | Setting Position instead of animating',
          '; vehicle id: ', record.id,
        );
        marker.setPosition(position);
        if (!noRedraw) {
          logd('FV:', 'updateMarkerByLocation | noRedraw is false, repainting cluster',
            '; vehicle id: ', record.id,
          );
          this.map.markerCluster.repaint();
        }
        resolve();
      }
      if (isSelected && !followMode) {
        gmap.panTo(position);
      }
    });
    return promise;
  }

  private animateMarker(
    marker: IKTMarker,
    position: google.maps.LatLng,
    animationDuration: number,
    followMode: boolean,
  ): Promise<AnimationPromiseType> {
    const animationInfo: IAnimationInfo = marker.animateTo(position, { duration: animationDuration, TAB_STATUS, followMode });
    logd('FV:', 'updateMarkerByLocation | Animating Marker:',
    '; animationDuration: ', animationDuration,
  );
    marker.animationPromise = animationInfo.promise;
    marker.cancelAnimation = animationInfo.cancelAnimation;
    return marker.animationPromise;
  }

  private toggleSelectionOfMarker(id: number, isSelected: boolean): Promise<void> {
    const record: IRecord = this.cachedMarkerRecordHash[id].record;
    const gmapsMarker: IKTMarker = this.cachedMarkerRecordHash[id].gmapsMarker;
    return this.generateAndSetImage(record, gmapsMarker, isSelected);
  }

  private getZIndex(isSelected: boolean): number {
    return isSelected ? selectedMarkerZIndex : unselectedMarkerZIndex;
  }

  private getPositionFromCoords(lat: number, lng: number): google.maps.LatLng {
    return new google.maps.LatLng(lat, lng);
  }

  private getPositionForRecord(record: IRecord): google.maps.LatLng {
    const { lat, lng }: { lat: number, lng: number } = record.currentLocation;
    return this.getPositionFromCoords(lat, lng);
  }

  private getMarkerZIndexForRecord(record: IRecord): number {
    return this.selectedId === record.id ? selectedMarkerZIndex : unselectedMarkerZIndex;
  }

  private createMarkersPromise(recordList: IRecord[]): Array<Promise<IKTMarker>> {
    const promises: Array<Promise<IKTMarker>> = [];
    recordList.forEach((record: IRecord) => {
      if (!record.currentLocation) return;
      const config: google.maps.MarkerOptions = {
        position: this.getPositionForRecord(record),
        zIndex: this.getMarkerZIndexForRecord(record) || undefined,
        visible: typeof record.visible === 'boolean' ? record.visible : true,
      };
      record.showAlert = this.shouldShowAlert(record);
      const markerCreatedPromise: Promise<IKTMarker> = new Promise(resolve => {
        this.generateImage(record, false).then((iconDrawingInfo: IGeneratedImage) => {
          resolve(this.createMarker(config, iconDrawingInfo, record));
        });
      });
      promises.push(markerCreatedPromise);
    });
    return promises;
  }

  private getAssetImageKey(record: IRecord, selected: boolean): VehicleImageKeyType {
    const moving: boolean = record.currentLocation.moving;
    const bearing: number = record.currentLocation.bearing;
    if (bearing != null && moving === true && selected === true) {
      return 'marker-driving-eld-enabled-glow';
    } else if (bearing != null && moving === true) return 'marker-driving-eld-enabled';
    else if (bearing != null && moving === false) return 'vehicle-stationary';

    return 'off-duty';
  }

  private getVehicleImageKey(record: IRecord, selected: boolean): VehicleImageKeyType {
    const motionDescription: string = record.currentLocation.motionDescription;
    const bearing: number = record.currentLocation.bearing;
    if (bearing != null && motionDescription !== 'Stationary' && selected === true) {
      return 'marker-driving-eld-enabled-glow';
    } else if (bearing != null && motionDescription !== 'Stationary') return 'marker-driving-eld-enabled';
    else if (bearing != null && motionDescription === 'Stationary') return 'vehicle-stationary';

    return 'off-duty';
  }

  private getDriverImageKey(record: IRecord): DriverImageKeyType {
    if (record.dutyStatus === 'driving') return 'driving-without-eld';
    else if (record.dutyStatus === 'on_duty' || record.dutyStatus === 'waiting') return 'driver-on-duty';

    return 'off-duty';
  }

  private generateImage(record: IRecord, isMarkerSelected: boolean): Promise<IGeneratedImage> {
    let imageName: string = null;
    const title: string = record[this.recordTitleKey];
    if (this.type === 'vehicles') imageName = this.getVehicleImageKey(record, isMarkerSelected);
    else if (this.type === 'assets') imageName = this.getAssetImageKey(record, isMarkerSelected);
    else if (this.type === 'drivers') imageName = this.getDriverImageKey(record);
    return generateImage(title, imageName, record.showAlert, record.currentLocation.bearing);
  }

  private shouldShowAlert(record: IRecord): boolean {
    if (this.type === 'vehicles') return !!((record.recentDvir.defects && !record.isDvirStatusOk) || record.faultCodesCount);
    else if (this.type === 'drivers') return false;
    else if (this.type === 'assets') return isFiniteNumber(record.batteryCapacity) && record.batteryCapacity <= 10;

    return false;
  }

  private getIconConfiguration(iconDrawingInfo: IGeneratedImage): IIconConfiguration {
    const { image, anchor }: { image: string; anchor: IGeneratedImageAnchor } = iconDrawingInfo;
    return {
      url: image,
      anchor: new (window as any).google.maps.Point(anchor.x, anchor.y),
      scaledSize: new (window as any).google.maps.Size(CANVAS_WIDTH, CANVAS_HEIGHT),
    };
  }

  private createMarker(config: google.maps.MarkerOptions, iconDrawingInfo: IGeneratedImage, record: IRecord): IKTMarker {
    const { shape }: { shape?: IGeneratedImageShape } = iconDrawingInfo;
    extend(config, {
      ktMarkerId: record.id,
      icon: this.getIconConfiguration(iconDrawingInfo),
      shape,
      optimized: false,
    });

    const markerObj: IKTMarker = new google.maps.Marker(config) as IKTMarker;
    this.cachedMarkerRecordHash[record.id] = {
      record,
      gmapsMarker: markerObj,
    };

    this.ngZone.runOutsideAngular(() => {
      this.gmaps.event.addListener(markerObj, 'click', () => {
        const selectedId: number = markerObj.ktMarkerId;
        this.onClick.emit(selectedId);
      });
    });

    return markerObj;
  }
}
